import React, { useEffect, useRef, useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "./components/Layout/Layout";
import PrivateRoute from "./routes/PrivateRoute";
import { toast, ToastContainer } from "react-toastify";
import Login from "./auth/Login";
import ForgotPassword from "./auth/ForgotPassword";
import StoreSelect from "./components/storeSelect/StoreSelect";
import PageNotFound from "./components/controls/PageNotFound";
import LazyLoader from "./components/controls/LazyLoader";
import NewHomeSection from ".//../src/components/onlineStore/homeSection/NewHomeSection";
import Addproduct from "./components/catalogue/product/Addproduct";
import FilterAttendance from "./components/staff/attendance/FilterAttendance";
import CaptureAttendance from "./components/staff/attendance/CaptureAttendance";
import OptionAttendance from "./components/staff/attendance/OptionAttendance";
import CashRegister from "./components/sales/cashRegister/CashRegister";
import Viewproduct from "./components/catalogue/product/ViewProduct/Viewproduct";
import ProductOverview from "./components/catalogue/product/ViewProduct/ProductOverview";
import AddStoreData from "./components/setup/store/Addstoredata";
import CustomerDetail from "./components/CRM/customer/CustomerDetail";
import AddCoupon from "./components/CRM/coupon/AddCoupon";
import WhatsappWalletFailed from "./components/controls/WhatsappWalletFailed";
import WhatsappWalletSuccess from "./components/controls/WhatsappWalletSuccess";
import GetStarted from "./components/setup/GetStarted";
import {
  hasCompleteUserData,
  initializeStorageCheck,
} from "./util/localStorageUtils";
import { fetchStore } from "./api/storeApi";
import { setStore, setStoresList } from "./redux/slices/storeSlice";
import PageLoading from "./components/controls/PageLoading";
import CustomerOutStandingLedger from "./components/reports/report/CustomerOutStandingLedger";
import CustomerWalletLedger from "./components/reports/report/CustomerWalletLedger";
import PublicInvoicePage from "./invoice/PublicInvoicePage";
import DashboardSetting from "./components/reports/dashboard/DashboardSetting";
import { onMessage } from "firebase/messaging";
import { messaging } from "../fcmFirebase";
import NotificationBox from "./components/notification/NotificationBox";
import CustomersDashboard from "./components/CRM/CustomersDashboard";
import KDS from "./components/sales/kot/KDS";
import KOTCustomerDisplay from "./components/sales/kot/KOTCustomerDisplay";
import Table from "./components/setup/table/Table";
import { selectUser } from "./redux/slices/userSlice";

// Lazy loaded pages with LazyLoader wrapper
const Dashboard = LazyLoader(
  React.lazy(() => import("./components/reports/dashboard/Dashboard"))
);

const Report = LazyLoader(
  React.lazy(() => import("./components/reports/report/Report"))
);

const LoyaltyDashboard = LazyLoader(
  React.lazy(() => import("./components/reports/report/LoyaltyDashboard"))
);

const MembershipDashboard = LazyLoader(
  React.lazy(() => import("./components/reports/report/MembershipDashboard"))
);

const InStockReport = LazyLoader(
  React.lazy(() => import("./components/reports/report/InStockReport"))
);

const EmployeeInsight = LazyLoader(
  React.lazy(() =>
    import("./components/reports/report/EmployeeReport/EmployeeInsight")
  )
);

const AttendanceReport = LazyLoader(
  React.lazy(() =>
    import("./components/reports/report/EmployeeReport/AttendanceReport")
  )
);

const EmployeeIncentive = LazyLoader(
  React.lazy(() =>
    import("./components/reports/report/EmployeeReport/EmployeeIncentive")
  )
);

const PurchaseProjectionReport = LazyLoader(
  React.lazy(() =>
    import("./components/reports/report/PurchaseProjectionReport")
  )
);

const AllReports = LazyLoader(
  React.lazy(() => import("./components/reports/report/AllReports"))
);
const DownloadedReports = LazyLoader(
  React.lazy(() => import("./components/reports/report/DownloadedReports"))
);
const ItemLedger = LazyLoader(
  React.lazy(() => import("./components/reports/report/ItemLedger"))
);

// sales
const NewSales = LazyLoader(
  React.lazy(() => import("./components/sales/newSales/NewSales"))
);
const RecievePayment = LazyLoader(
  React.lazy(() => import("./components/sales/recievePayment/RecievePayment"))
);

const ViewSales = LazyLoader(
  React.lazy(() => import("./components/sales/salesListPage/ViewSales"))
);

const Packages = LazyLoader(
  React.lazy(() => import("./components/sales/packages/Packages"))
);

const SalesTransactionList = LazyLoader(
  React.lazy(() =>
    import("./components/sales/salesListPage/SalesTransactionList")
  )
);
const DeliveryList = LazyLoader(
  React.lazy(() => import("./components/sales/deliveryList/DeliveryList"))
);

const OnlineOrders = LazyLoader(
  React.lazy(() => import("./components/onlineOrders/OnlineOrders"))
);
const CashRegisterClosingList = LazyLoader(
  React.lazy(() =>
    import("./components/sales/cashRegisterClosingList/CashRegisterClosingList")
  )
);

// crm
const Customer = LazyLoader(
  React.lazy(() => import("./components/CRM/customer/Customer"))
);
const Campaign = LazyLoader(
  React.lazy(() => import("./components/CRM/campaign/Campaign"))
);
const CreateCampaign = LazyLoader(
  React.lazy(() => import("./components/CRM/campaign/CreateCampaign"))
);
const Membership = LazyLoader(
  React.lazy(() => import("./components/CRM/membership/Membership"))
);
const AddMembership = LazyLoader(
  React.lazy(() => import("./components/CRM/membership/AddMembership"))
);
const Coupon = LazyLoader(
  React.lazy(() => import("./components/CRM/coupon/Coupon"))
);
const MessageTemplate = LazyLoader(
  React.lazy(() => import("./components/CRM/messageTemplate/MessageTemplate"))
);
const CampaignManage = LazyLoader(
  React.lazy(() => import("./components/CRM/campaign/CampaignManage"))
);

// inventory
const NewInventory = LazyLoader(
  React.lazy(() =>
    import("./components/inventory/newInventory/NewInventorynew")
  )
);
const InventoryList = LazyLoader(
  React.lazy(() => import("./components/inventory/inventoryList/InventoryList"))
);
const InTransit = LazyLoader(
  React.lazy(() => import("./components/inventory/inTransit/InTransit"))
);
const Vendor = LazyLoader(
  React.lazy(() => import("./components/inventory/vendor/Vendor"))
);
const AddVendor = LazyLoader(
  React.lazy(() => import("./components/inventory/vendor/AddVendor"))
);
const ViewInventory = LazyLoader(
  React.lazy(() => import("./components/inventory/inventoryList/ViewInventory"))
);
const PrintBarcode = LazyLoader(
  React.lazy(() => import("./components/inventory/inventoryList/PrintBarcode"))
);

// loyalty
const LoyaltyType = LazyLoader(
  React.lazy(() => import("./components/loyalty/LoyaltyType"))
);
const LoyaltySetup = LazyLoader(
  React.lazy(() => import("./components/loyalty/LoyaltySetup"))
);
const AddLoyaltyType = LazyLoader(
  React.lazy(() => import("./components/loyalty/AddLoyaltyType"))
);

// catalogue
const Product = LazyLoader(
  React.lazy(() => import("./components/catalogue/product/Product"))
);
const ImportProduct = LazyLoader(
  React.lazy(() => import("./components/catalogue/product/ImportProduct"))
);

const Category = LazyLoader(
  React.lazy(() => import("./components/catalogue/category/Category"))
);
const SubCategory = LazyLoader(
  React.lazy(() => import("./components/catalogue/subcategory/SubCategory"))
);
const Brand = LazyLoader(
  React.lazy(() => import("./components/catalogue/brand/Brand"))
);
const Taxes = LazyLoader(
  React.lazy(() => import("./components/catalogue/taxes/Taxes"))
);
const ProductGroup = LazyLoader(
  React.lazy(() => import("./components/catalogue/productGroup/ProductGroup"))
);
const Offers = LazyLoader(
  React.lazy(() => import("./components/catalogue/offers/Offers"))
);
const CreateOffer = LazyLoader(
  React.lazy(() => import("./components/catalogue/offers/CreateOffer"))
);
const ProductQuickEdit = LazyLoader(
  React.lazy(() => import("./components/catalogue/qucikedit/ProductQuickEdit"))
);

const PincodeProductQuickEdit = LazyLoader(
  React.lazy(() =>
    import("./components/catalogue/qucikedit/PincodeProduxrQuickEdit")
  )
);

const Bom = LazyLoader(
  React.lazy(() => import("./components/catalogue/bom/Bom"))
);
// {appointment }
const Appoitnment = LazyLoader(
  React.lazy(() => import("./components/appointment/Appointment"))
);

// expense
const ExpenseList = LazyLoader(
  React.lazy(() => import("./components/expenses/expenseList/ExpenseList"))
);
const AccountManagement = LazyLoader(
  React.lazy(() =>
    import("./components/expenses/accountManagement/AccountManagement")
  )
);
const ExpenseCategory = LazyLoader(
  React.lazy(() =>
    import("./components/expenses/expenseCategory/ExpenseCategory")
  )
);
const Account = LazyLoader(
  React.lazy(() => import("./components/expenses/account/Account"))
);

// staff
const Attendance = LazyLoader(
  React.lazy(() => import("./components/staff/attendance/Attendance"))
);
const StaffDetails = LazyLoader(
  React.lazy(() => import("./components/staff/staffDetails/StaffDetails"))
);

// online store
const Profile = LazyLoader(
  React.lazy(() => import("./components/onlineStore/profile/Profile"))
);
const Menu = LazyLoader(
  React.lazy(() => import("./components/onlineStore/menu/Menu"))
);
const Slider = LazyLoader(
  React.lazy(() => import("./components/onlineStore/slider/Slider"))
);
const HomeSection = LazyLoader(
  React.lazy(() => import("./components/onlineStore/homeSection/HomeSection"))
);
const ProductDetails = LazyLoader(
  React.lazy(() =>
    import("./components/onlineStore/productDetails/ProductDetails")
  )
);
const Payment = LazyLoader(
  React.lazy(() => import("./components/onlineStore/payment/Payment"))
);
const Pages = LazyLoader(
  React.lazy(() => import("./components/onlineStore/pages/Pages"))
);
const Settings = LazyLoader(
  React.lazy(() => import("./components/onlineStore/settings/Settings"))
);
const PaymentGateway = LazyLoader(
  React.lazy(() =>
    import("./components/onlineStore/paymentGateway/PaymentGateway")
  )
);

// setup
const Registration = LazyLoader(
  React.lazy(() => import("./components/setup/registration/Registration"))
);
const Store = LazyLoader(
  React.lazy(() => import("./components/setup/store/Store"))
);
const BarcodeSettings = LazyLoader(
  React.lazy(() =>
    import("./components/setup/store/PrintConfig/BarcodeSettings")
  )
);
const PrintConfigList = LazyLoader(
  React.lazy(() =>
    import("./components/setup/store/PrintConfig/PrintConfigList")
  )
);
const PrintConfig = LazyLoader(
  React.lazy(() => import("./components/setup/store/PrintConfig/PrintConfig"))
);

const NewPrintTemplate = LazyLoader(
  React.lazy(() =>
    import("./components/setup/store/PrintConfig/NewPrintTemplate")
  )
);

const PaymentType = LazyLoader(
  React.lazy(() => import("./components/setup/paymentTypes/PaymentType"))
);
const CustomRoles = LazyLoader(
  React.lazy(() => import("./components/setup/customRoles/CustomRoles"))
);
const ModuleConfiguration = LazyLoader(
  React.lazy(() =>
    import("./components/setup/moduleConfiguration/ModuleConfiguration")
  )
);
const CustomField = LazyLoader(
  React.lazy(() => import("./components/setup/customField/CustomField"))
);
const TransactionSmsTemplate = LazyLoader(
  React.lazy(() =>
    import("./components/setup/transactionSMSTemplate/TransactionSmsTemplate")
  )
);
const AdditionalSetting = LazyLoader(
  React.lazy(() =>
    import("./components/setup/additionalSetting/AdditionalSetting")
  )
);
const Integration = LazyLoader(
  React.lazy(() => import("./components/integration/Integration"))
);
const WhatsappWalletCreditPurchase = LazyLoader(
  React.lazy(() =>
    import("./components/integration/whatsapp/WhatsappWalletCreditPurchase")
  )
);
const CredentialVerify = LazyLoader(
  React.lazy(() => import("./components/integration/CredentialVerify"))
);
const Template = LazyLoader(
  React.lazy(() => import("./components/integration/Template"))
);

const SalesChannel = LazyLoader(
  React.lazy(() => import("./components/setup/salesChannel/SalesChannel"))
);

const PaymentOptionsPage = LazyLoader(
  React.lazy(() => import("./payment/PaymentOptionsPage"))
);
const WhatsappPayment = LazyLoader(
  React.lazy(() => import("./payment/paymentsPage/WhatsappPayment"))
);
const SmsPayment = LazyLoader(
  React.lazy(() => import("./payment/paymentsPage/SmsPayment"))
);
const PlaneRenewalPayment = LazyLoader(
  React.lazy(() => import("./payment/paymentsPage/PlaneRenewalPayment"))
);

const PaymentCredentials = LazyLoader(
  React.lazy(() =>
    import("./components/integration/paymentGateway/PaymentCredentials")
  )
);

const LoyaltyCredentials = LazyLoader(
  React.lazy(() =>
    import("./components/integration/loyalty/LoyaltyCredentials")
  )
);

const WarehouseCredentials = LazyLoader(
  React.lazy(() =>
    import("./components/integration/warehouse/WarehouseCredentials")
  )
);

const AdsrCredentials = LazyLoader(
  React.lazy(() =>
    import("./components/integration/adsrIntegration/AdsrCredentials")
  )
);

const App = () => {
  const [loading, setLoading] = useState(false);
  const storesList = useSelector((state) => state.store.storesList);
  const [showNotificationBox,setShowNotificationBox]=useState(false);
  const [notificationData,setNotificationData]=useState({});
  const audioRef = useRef(null);


  const singleStore = useSelector((state) => state.store.store);
  const user=useSelector(selectUser);
  const dispatch = useDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState(false);


 //const audioUrl = "https://firebasestorage.googleapis.com/v0/b/posible-app-prod.appspot.com/o/notification_sound%2Fswiggy_new_order.mp3?alt=media&token=53608d89-7d44-441d-a7d1-38c39aa7f6dd";

useEffect(() => {
 

  const unsubscribe = onMessage(messaging, (payload) => {
    console.log("Foreground message:", payload);

    const title = payload.notification?.title || "";
    const body = payload.notification?.body || "";
    const parsedData=JSON.parse(payload?.data?.payload) || {};
    console.log("parsedData?.audioUrl",parsedData?.audioUrl)
  
     audioRef.current = new Audio(parsedData?.audioUrl);
  audioRef.current.loop = true;

    setNotificationData(parsedData);
    setShowNotificationBox(true);

    // if (Notification.permission === "granted") {
    //   new Notification(title, { body });
    // }

    // 🔊 Play sound
    audioRef.current.play().catch((err) => {
      console.log("Autoplay blocked:", err);
    });

    // 🕒 Auto stop after 1 minute
    setTimeout(() => {
  if (audioRef.current) {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
  }
}, 5000); // ✅ 5 seconds

  });

  return () => unsubscribe();
}, []);
const stopSound = () => {
  if (audioRef.current) {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
  }
};
const onClose = () => {
  stopSound();                // Stop sound
  setShowNotificationBox(false);  // Hide notification box
};


  const fetchStores = async () => {
    try {
      setLoading(true);
      const storeData = await fetchStore();
      if (storeData.code == "0") {
        dispatch(setStoresList(storeData.stores));
        const store = storeData.stores.find(
          (store) => store.key === singleStore.key
        );

        dispatch(setStore(store));
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      toast.error("Plz logout and login again");
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeStorageCheck();

    if (hasCompleteUserData()) {
      fetchStores();
    }
  }, []);

  document.addEventListener(
    "wheel",
    function (event) {
      if (document.activeElement.type === "number") {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            loading ? (
              <PageLoading />
            ) : isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <Login />
            )
          }
        />
        {/* <Route path="/" element={<Login />} /> */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/public/invoice/:key" element={<PublicInvoicePage />} />

        {/* Protected Routes with Layout */}
        <Route element={<PrivateRoute />}>
          <Route element={<Layout />}>
            <Route path="/home" element={<Dashboard />} />
             <Route path="/reports/customeroutstanding" element={<CustomerOutStandingLedger />} />
             <Route path="/dashboard/setting" element={<DashboardSetting />} />
            <Route
              path="/store-selection"
              element={
                storesList?.length > 0 ? (
                  <StoreSelect />
                ) : (
                  <Navigate to="/home" replace />
                )
              }
            />
            <Route path="/reports" element={<Report />} />
                     <Route path="/sales/kds" element={<KDS />} />
                <Route path="/sales/kdscustomerdisplay" element={<KOTCustomerDisplay />} />
            <Route path="/reports/loyaltydashboard" element={<LoyaltyDashboard />} />
            <Route path="/reports/membershipdashboard" element={<MembershipDashboard />} />
            <Route path="/reports/emplyoeeinsight" element={<EmployeeInsight />} />
            <Route path="/reports/instockreport" element={<InStockReport />} />
            <Route
              path="/reports/attendancereport"
              element={<AttendanceReport />}
            />
            <Route
              path="/reports/employeeincentive"
              element={<EmployeeIncentive />}
            />
            <Route
              path="/reports/purchaseprojectionreport"
              element={<PurchaseProjectionReport />}
            />
            <Route path="/reports/allreports" element={<AllReports />} />
            <Route
              path="/reports/downloadedreports"
              element={<DownloadedReports />}
            />
            <Route path="/reports/itemledger" element={<ItemLedger />} />

            {/* {payment routes } */}
            <Route
              path="/payment/payment-options"
              element={<PaymentOptionsPage />}
            />
            <Route
              path="/payment/payment-options/sms"
              element={<SmsPayment />}
            />
            <Route
              path="/payment/payment-options/whatsapp"
              element={<WhatsappPayment />}
            />
            {/* <Route
              path="/payment/payment-options/plan-renewal"
              element={<PlaneRenewalPayment />}
            /> */}

            {/* sales route  */}
            <Route path="/sales/sales" element={<CashRegister />} />
            <Route path="/sales/newsales" element={<NewSales />} />
            <Route path="/sales/receivepayment" element={<RecievePayment />} />
            <Route
              path="/sales/salestransactionlist"
              element={<SalesTransactionList />}
            />
            <Route path="/sales/viewsales" element={<ViewSales />} />
            <Route path="/packages" element={<Packages />} />

            <Route path="/sales/deliverylist" element={<DeliveryList />} />
            <Route path="/onlineorders" element={<OnlineOrders />} />
            <Route
              path="/sales/cashregisterclosinglist"
              element={<CashRegisterClosingList />}
            />

            {/* inetagration  */}
            <Route path="/integration" element={<Integration />} />
            <Route
              path="/integration/whatsapp/credentials-verify"
              element={<CredentialVerify />}
            />
            <Route
              path="/integration/phonepe/credentials-verify"
              element={<PaymentCredentials />}
            />
            <Route
              path="/integration/loyalty/credentials-verify"
              element={<LoyaltyCredentials />}
            />
            <Route
              path="/integration/warehouse/credentials-verify"
              element={<WarehouseCredentials />}
            />
            <Route
              path="/integration/adsrIntegration/credentials-verify"
              element={<AdsrCredentials />}
            />
            <Route
              path="/integration/templates/:providerName/:providerId"
              element={<Template />}
            />
            <Route
              path="/whatsapp/wallet/credit_purchase"
              element={<WhatsappWalletCreditPurchase />}
            />
            <Route
              path="/whatsapp/payment/failed"
              element={<WhatsappWalletFailed />}
            />
            <Route
              path="/whatsapp/payment/success"
              element={<WhatsappWalletSuccess />}
            />

            <Route path="/setup/saleschannel" element={<SalesChannel />} />

            {/* crm routes */}
            <Route path="/crm/customers" element={<Customer />} />
              <Route path="/crm/customerdashboard" element={<CustomersDashboard />} />
            <Route path="/crm/customerdetail" element={<CustomerDetail />} />
            <Route path="/crm/campaigns" element={<Campaign />} />
            <Route path="/crm/campaign/create" element={<CreateCampaign />} />
            <Route
              path="/crm/campaign/edit/:campaignId"
              element={<CreateCampaign />}
            />
            <Route
              path="/crm/campaign/manage/:campaignId"
              element={<CampaignManage />}
            />
            <Route path="/crm/memberships" element={<Membership />} />
            <Route path="/crm/addmembership" element={<AddMembership />} />
            <Route path="/crm/Coupons" element={<Coupon />} />
            <Route path="/crm/addcoupon" element={<AddCoupon />} />

            <Route path="/crm/message-template" element={<MessageTemplate />} />

            {/* inventory  */}
            <Route path="/inventory/newinventory" element={<NewInventory />} />
            <Route path="/inventory/editinventory" element={<NewInventory />} />
            <Route
              path="/inventory/inventorylist"
              element={<InventoryList />}
            />
              <Route
              path="/inventory/intransit"
              element={<InventoryList />}
            />
            <Route
              path="/inventory/inventorylist/viewinventory/:id"
              element={<ViewInventory />}
            />
            <Route path="/settings/vendors" element={<Vendor />} />
            <Route
              path="/settings/vendors/addvendors"
              element={<AddVendor />}
            />
            <Route
              path="/settings/vendors/editvendor:vendorId"
              element={<AddVendor />}
            />
            <Route path="/printbarcode" element={<PrintBarcode />} />

            {/* loyalty */}
            <Route path="/loyalty/loyaltytype" element={<LoyaltyType />} />
            <Route path="/loyalty/setup" element={<LoyaltySetup />} />
            <Route path="/loyalty/add" element={<AddLoyaltyType />} />
            {/* {appointment } */}
            <Route path="/appointment" element={<Appoitnment />} />

            {/* expense  */}
            <Route path="/expense/expenselist" element={<ExpenseList />} />
            <Route
              path="/expense/transactionlist"
              element={<AccountManagement />}
            />
            <Route
              path="/expense/categorieslist"
              element={<ExpenseCategory />}
            />
            <Route path="/expense/accounts" element={<Account />} />

            {/* staff  */}
            <Route path="/staff/attendancelist" element={<Attendance />} />
            <Route path="/settings/employees/" element={<StaffDetails />} />
            <Route path="./FilterAttendance" element={<FilterAttendance />} />
            <Route
              path="../attendance/CaptureAttendance"
              element={<CaptureAttendance />}
            />
            <Route path="./OptionAttendance" element={<OptionAttendance />} />

            {/* online store  */}
            <Route path="/online-store/profile" element={<Profile />} />
            <Route path="/online-store/menus" element={<Menu />} />
            <Route path="/online-store/slider" element={<Slider />} />
            <Route
              path="/online-store/homesections"
              element={<HomeSection />}
            />
            <Route
              path="/online-store/productdetail"
              element={<ProductDetails />}
            />
            <Route path="/online-store/payment" element={<Payment />} />
            <Route path="/online-store/pages" element={<Pages />} />
            <Route path="/online-store/settings" element={<Settings />} />
            <Route
              path="/online-store/onlinepaymenttransactions"
              element={<PaymentGateway />}
            />

            {/* catalogue  */}
            <Route path="/catalogue/product" element={<Product />} />
            <Route
              path="/catalogue/product/ImportProduct"
              element={<ImportProduct />}
            />
            <Route
              path="/catalogue/product/Viewproduct"
              element={<Viewproduct />}
            />
            <Route
              path="/catalogue/product/ImportProduct"
              element={<ImportProduct />}
            />
            <Route
              path="/catalogue/product/Addproduct/:key"
              element={<Addproduct />}
            />
            <Route
              path="/catalogue/product/Addproduct"
              element={<Addproduct />}
            />
            <Route path="/catalogue/category" element={<Category />} />
            <Route path="/catalogue/subCategory" element={<SubCategory />} />
            <Route path="/catalogue/brands" element={<Brand />} />
            <Route path="/catalogue/taxes" element={<Taxes />} />
            <Route path="/catalogue/product-group" element={<ProductGroup />} />
            <Route path="/catalogue/offers" element={<Offers />} />
            <Route
              path="/catalogue/offers/newoffer"
              element={<CreateOffer />}
            />
            <Route
              path="/catalogue/productquickedit"
              element={<ProductQuickEdit />}
            />
            <Route
              path="/catalogue/pincodeproductquickedit"
              element={<PincodeProductQuickEdit />}
            />
            <Route path="/catalogue/bom" element={<Bom />} />

            {/*product view pages*/}
            <Route
              path="/catalogue/product/Viewproduct/Overview"
              element={<ProductOverview />}
            />

            {/* setup */}
            <Route path="/settings/registration" element={<Registration />} />
            <Route path="/settings/stores" element={<Store />} />
            <Route
              path="/settings/printconfiglist"
              element={<PrintConfigList />}
            />
            <Route path="/settings/printconfig" element={<PrintConfig />} />
            <Route
              path="/settings/newprinttemplates"
              element={<NewPrintTemplate />}
            />
            <Route
              path="/settings/barcodesetting"
              element={<BarcodeSettings />}
            />

            <Route path="/settings/addstore" element={<AddStoreData />} />

            <Route path="/settings/paymenttypes" element={<PaymentType />} />
            <Route path="/settings/customroles" element={<CustomRoles />} />
            <Route
              path="/settings/moduleconfig"
              element={<ModuleConfiguration />}
            />
            <Route path="/settings/customfields" element={<CustomField />} />
            <Route
              path="/settings/transactionalsmstemplates"
              element={<TransactionSmsTemplate />}
            />
            <Route
              path="/settings/addtionalsettings"
              element={<AdditionalSetting />}
            />

            <Route path="/setup/getstarted" element={<GetStarted />} />
                 <Route path="/setup/table" element={<Table />} />
            <Route
              path="/onlinestore/NewHomeSection"
              element={<NewHomeSection />}
            />
          </Route>
        </Route>

        {/* <Route
          path="/settings/stores/printconfig/addprintconfig"
          element={<PrintConfig />}
        /> */}

        {/* <Route
          path="/settings/stores/printconfig/editprintconfig/:id"
          element={<PrintConfig />}
        /> */}

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    <NotificationBox
  visible={showNotificationBox}
  data={notificationData}
  onStop={stopSound}
  onClose={onClose}
/>


      <ToastContainer
        position="top-center"
        draggable
        pauseOnHover
        theme="light"
        closeOnClick={"true"}
        autoClose={5000}
      />
    </>
  );
};

export default App;
